import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  addQuestionGame,
  updateQuestionGame,
  getQuestionGames,
} from "../../services/questionGameService";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import { getSectionQuestions } from "../../services/sectionQuestionsService";

export const AddquestiongameLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [sectionCQuestions, setSectionCQuestions] = useState([]);
  const [options, setOptions] = useState([]);

  const [form, setForm] = useState({
    league_id: "",
    match_id: "",
    game_title: "",
    points_rate: "50",          // DEFAULT 50
    admin_percent: "20",        // DEFAULT 20%
    winner_percentage: "5",     // DEFAULT 5%
    status: "live",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [matchRes, leagueRes, secRes] = await Promise.all([
      getMatches(),
      getLeagues(),
      getSectionQuestions("C"),
    ]);

    if (matchRes?.status) setMatches(matchRes.data || []);
    if (leagueRes?.status) setLeagues(leagueRes.data || []);
    if (secRes?.status) setSectionCQuestions(secRes.data || []);
  };

  const leagueMatches = useMemo(() => {
    if (!form.league_id) return [];
    return matches.filter(
      (m) => String(m.league_id) === String(form.league_id)
    );
  }, [form.league_id, matches]);

  const leagueQuestions = useMemo(() => {
    if (!form.league_id) return sectionCQuestions;
    return sectionCQuestions.filter(
      (q) =>
        !q.league_id ||
        String(q.league_id) === String(form.league_id)
    );
  }, [form.league_id, sectionCQuestions]);

  const handleQuestionSelect = (text) => {
    const selected = sectionCQuestions.find(
      (q) => q.question === text
    );

    if (!selected) return;

    let parsed = [];
    try {
      parsed = selected.options_json
        ? JSON.parse(selected.options_json)
        : [];
    } catch {}

    setOptions(parsed);
    setForm({ ...form, game_title: text });
  };

  useEffect(() => {
    if (!id) return;

    getQuestionGames().then((res) => {
      const game = res?.data?.find(
        (g) => String(g.id) === String(id)
      );
      if (!game) return;

      setForm({
        league_id: String(game.league_id ?? ""),
        match_id: String(game.match_id ?? ""),
        game_title: game.game_title ?? "",
        points_rate: String(game.points_rate ?? "50"),
        admin_percent: String(game.admin_percent ?? "20"),
        winner_percentage: String(game.winner_percentage ?? "5"),
        status: game.status ?? "live",
      });

      setOptions(game.ans_options || []);
    });
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...form,
      ans_options: options,
    };

    const res = id
      ? await updateQuestionGame(id, payload)
      : await addQuestionGame(payload);

    if (res?.status === true) {
      toast.success(
        id ? "Updated Successfully" : "Added Successfully"
      );
      navigate("/questiongame-list");
    } else {
      toast.error(res?.message || "Operation failed");
    }

    setLoading(false);
  };

  return (
    <div className="container mt-4">
      <div className="card shadow-sm">
        <div className="card-body">
          <h4 className="mb-4">
            {id ? "Update Question Game" : "Add Question Game"}
          </h4>

          <form onSubmit={handleSubmit} className="row g-3">

            <div className="col-md-6">
              <label>Select League</label>
              <select
                className="form-control"
                value={form.league_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    league_id: e.target.value,
                    match_id: "",
                  })
                }
              >
                <option value="">Select League</option>
                {leagues.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.league_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label>Select Match</label>
              <select
                className="form-control"
                value={form.match_id}
                onChange={(e) =>
                  setForm({ ...form, match_id: e.target.value })
                }
              >
                <option value="">Select Match</option>
                {leagueMatches.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.team_a} vs {m.team_b}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label>Select Question</label>
              <select
                className="form-control"
                value={form.game_title}
                onChange={(e) =>
                  handleQuestionSelect(e.target.value)
                }
              >
                <option value="">Select Question</option>
                {leagueQuestions.map((q) => (
                  <option key={q.id} value={q.question}>
                    {q.question}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label>Points Rate</label>
              <select
                className="form-control"
                value={form.points_rate}
                onChange={(e) =>
                  setForm({
                    ...form,
                    points_rate: e.target.value,
                  })
                }
              >
                {[50, 100, 250, 500].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label>Admin %</label>
              <select
                className="form-control"
                value={form.admin_percent}
                onChange={(e) =>
                  setForm({
                    ...form,
                    admin_percent: e.target.value,
                  })
                }
              >
                {[20, 30, 40, 50].map((v) => (
                  <option key={v} value={v}>
                    {v}%
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label>Winner %</label>
              <select
                className="form-control"
                value={form.winner_percentage}
                onChange={(e) =>
                  setForm({
                    ...form,
                    winner_percentage: e.target.value,
                  })
                }
              >
                {[5, 6, 7, 8, 9, 10].map((v) => (
                  <option key={v} value={v}>
                    {v}%
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12">
              <label>Answer Options</label>
              {options.map((o) => (
                <input
                  key={o.id}
                  className="form-control mb-2"
                  value={o.option}
                  readOnly
                />
              ))}
            </div>

            <div className="col-12">
              <button
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading
                  ? "Please wait..."
                  : id
                  ? "Update Game"
                  : "Add Game"}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};